import { Close, History, Search as SearchIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import ServiceQuoteForm from "../../proposal/service-quote-form/ServiceQuoteForm";
import { UserIcon } from "./icons/UserIcon";
import { User } from "../../../store/slice/AuthSlice";

const AppHomeContent = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;

  const users = useSelector((state: RootState) => state.users.users);
  const userDetails = users.find((u) => u.id === user?.id);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("recentSearches");
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim() && recentSearches.length > 0) {
      setShowRecentSearches(true);
    } else {
      setShowRecentSearches(false);
    }
  }, [searchQuery, recentSearches]);

  const getCurrentDate = () => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    return `${days[now.getDay()]}, ${
      months[now.getMonth()]
    } ${now.getDate()}, ${now.getFullYear()}`;
  };

  const saveRecentSearch = (query: string) => {
    if (!query.trim()) return;

    const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(
      0,
      5,
    );

    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log("Searching for:", searchQuery);
      saveRecentSearch(searchQuery);
      setShowRecentSearches(false);
    }
  };

  const handleSearchFocus = () => {
    if (recentSearches.length > 0) {
      setShowRecentSearches(true);
    }
  };

  const handleRecentSearchClick = (search: string) => {
    setSearchQuery(search);
    setShowRecentSearches(false);
    console.log("Selected recent search:", search);
  };

  const handleRemoveRecentSearch = (
    searchToRemove: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    const updated = recentSearches.filter((s) => s !== searchToRemove);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  const handleClearAllSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("recentSearches");
    setShowRecentSearches(false);
  };

  return (
    <Box
      sx={{
        width: "85%",
        mx: "auto",
        minHeight: "85vh",
        backgroundColor: colors.primary[900],
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 4 },
        transform: { xs: "scale(0.9)", md: "scale(0.88)" },
        transformOrigin: "top center",
        transition: "transform 0.3s ease",
      }}
    >
      {/* Welcome Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          mt: 2,
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: "bold",
              color: colors.primary[700],
              fontSize: { xs: "3rem", md: "4rem" },
            }}
          >
            Hi
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderRadius: "50px",
              pr: 3,
              pl: 1,
              py: 1,
              border: `1px solid ${colors.primary[700]}`,
            }}
          >
            <Avatar
              src={userDetails?.profile_picture_url || undefined}
              sx={{
                width: 44,
                height: 44,
                backgroundColor: colors.primary[700],
              }}
            >
              {!userDetails?.profile_picture_url && (
                <UserIcon sx={{ color: "white" }} />
              )}
            </Avatar>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary[100],
                  fontSize: "0.95rem",
                  lineHeight: 1.2,
                }}
              >
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.primary[700],
                  fontSize: "0.75rem",
                  textTransform: "capitalize",
                }}
              >
                {userDetails?.role || "User"}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: colors.primary[700],
            fontSize: "0.9rem",
          }}
        >
          {` Today's date is ${getCurrentDate()} `}
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box
        ref={searchContainerRef}
        sx={{
          maxWidth: "540px",
          mx: "auto",
          mb: 12,
          position: "relative",
        }}
      >
        <Paper
          component="form"
          onSubmit={handleSearch}
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            borderRadius: 50,
            border: `2px solid #D9D9D9`,
            px: 3,
            py: 2,
            backgroundColor: colors.primary[900],
          }}
        >
          <TextField
            fullWidth
            inputRef={searchInputRef}
            placeholder="Search bookings, contacts, and venues..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleSearchFocus}
            variant="standard"
            autoComplete="off"
            inputProps={{
              autoCorrect: "off",
              autoCapitalize: "off",
              spellCheck: "false",
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon
                    sx={{ color: colors.primary[700], fontSize: 24, ml: 1 }}
                  />
                </InputAdornment>
              ),
              disableUnderline: true,
              sx: {
                fontSize: "1rem",
                color: colors.primary[700],
                "& ::placeholder": {
                  color: "#D9D9D9",
                  opacity: 1,
                },
              },
            }}
          />
        </Paper>

        {/* Recent Searches Dropdown */}
        {showRecentSearches && recentSearches.length > 0 && (
          <ClickAwayListener onClickAway={() => setShowRecentSearches(false)}>
            <Paper
              elevation={4}
              sx={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                zIndex: 1300,
                borderRadius: "0 0 24px 24px",
                border: `2px solid ${colors.primary[400]}`,
                borderTop: "none",
                backgroundColor: colors.primary[900],
                maxHeight: "300px",
                overflowY: "auto",
                mt: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 3,
                  pt: 2,
                  pb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[500],
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Recent Searches
                </Typography>
                <Typography
                  variant="caption"
                  onClick={handleClearAllSearches}
                  sx={{
                    color: colors.primary[400],
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Clear all
                </Typography>
              </Box>

              <List sx={{ py: 0 }}>
                {recentSearches.map((search, index) => (
                  <ListItem
                    key={index}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleRemoveRecentSearch(search, e)}
                        sx={{
                          color: colors.grey[500],
                          "&:hover": {
                            color: colors.grey[300],
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      onClick={() => handleRecentSearchClick(search)}
                      sx={{
                        py: 1.5,
                        px: 3,
                        "&:hover": {
                          backgroundColor: colors.primary[800],
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <History
                          sx={{
                            color: colors.grey[500],
                            fontSize: 20,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={search}
                        primaryTypographyProps={{
                          sx: {
                            color: colors.grey[100],
                            fontSize: "0.95rem",
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        )}
      </Box>

      <Box
        sx={{
          maxWidth: "1000px",
          mx: "auto",
        }}
      >
        <ServiceQuoteForm
          buttonText="Create Booking"
          buttonColor={
            !userDetails?.profile_picture_url ? colors.primary[700] : undefined
          }
        />
      </Box>
    </Box>
  );
};

export default AppHomeContent;
